import { useCallback, useEffect, useState } from 'react';

/**
 * Offering to install the app to the home screen.
 *
 * The manifest and service worker already make this app installable. Two things now offer
 * it, and they cover different people:
 *
 *   THE BROWSER'S OWN. Chrome and Edge raise their own install UI when a site qualifies,
 *   unless the site calls `preventDefault()` on `beforeinstallprompt` — so this app no
 *   longer does. That is the offer a first-time visitor sees, and it arrives without this
 *   app having to decide anything.
 *
 *   THIS CARD. `prompt()` must be called from a user action, so an install dialog cannot
 *   be opened on a timer — a button is what opens it. It is the route on a browser whose
 *   own offer is a quiet address-bar icon, and on iOS it is the only route of any kind:
 *   Safari fires no event and offers nothing, so it gets the share-sheet steps instead of
 *   a button that could not work.
 */

/** What, if anything, to offer this visitor. */
export type InstallOffer = 'prompt' | 'ios' | null;

export interface OfferInput {
  /** A deferred `beforeinstallprompt` is in hand: the browser will install on request. */
  hasPrompt: boolean;
  /** iOS, where the event never fires and the passenger has to use the share sheet. */
  isIos: boolean;
  /** Already launched from the home screen — there is nothing left to install. */
  isStandalone: boolean;
  /** Epoch ms until which the passenger asked not to be bothered. */
  dismissedUntil: number | null;
  now: number;
}

/**
 * Decides the offer, separately from any browser API, because every interesting case here is
 * a combination of circumstances rather than a single flag — and because the one thing worse
 * than never offering to install is offering repeatedly to somebody who already has.
 */
export function installOffer(input: OfferInput): InstallOffer {
  // Running as an installed app. Asking here would be asking someone to install the thing
  // they are already holding.
  if (input.isStandalone) return null;
  if (input.dismissedUntil !== null && input.now < input.dismissedUntil) return null;

  // A real prompt beats instructions wherever one exists, including on an iPad running a
  // browser that supports it.
  if (input.hasPrompt) return 'prompt';
  return input.isIos ? 'ios' : null;
}

/** Long enough that a "not now" is respected, short enough that it is not a permanent no. */
export const DISMISS_DAYS = 30;
const STORAGE_KEY = 'busbook.install.dismissedUntil';

export function readDismissedUntil(): number | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    // Private-mode browsers refuse storage. Offering again next visit is the safe failure.
    return null;
  }
}

export function writeDismissedUntil(until: number): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(until));
  } catch {
    /* see readDismissedUntil */
  }
}

/**
 * iPadOS 13 and later report themselves as Macintosh. The touch-point count is what still
 * tells the two apart, and getting this wrong shows share-sheet instructions to somebody
 * sitting at a desktop with no share sheet.
 */
export function isIosDevice(ua: string, maxTouchPoints: number): boolean {
  if (/iphone|ipod|ipad/i.test(ua)) return true;
  return /macintosh/i.test(ua) && maxTouchPoints > 1;
}

/** The event Chrome fires and then waits on. Not in the DOM lib's types. */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function useInstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissedUntil, setDismissedUntil] = useState<number | null>(() => readDismissedUntil());
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onBeforeInstall = (event: Event) => {
      /*
       * DELIBERATELY NOT preventDefault().
       *
       * That call is the only thing that suppresses the browser's own install UI -- the
       * bar Android Chrome raises by itself, the install control desktop browsers put in
       * the address bar. Calling it bought this app control over WHEN to ask, and paid
       * for it by never being asked at all on a first visit, because the only place the
       * replacement card rendered was behind a booking somebody had already made.
       *
       * So the browser's own offer is left alone. It appears on the browser's schedule,
       * to a visitor who has done nothing yet, which is the case the card could never
       * reach.
       *
       * The event is still kept, and that is not redundant. `prompt()` "must be called in
       * the event handler for a user action", so an install dialog can never be opened on
       * a timer -- our own button is the user action that opens it. It is the only route
       * on a browser whose native offer is a quiet address-bar icon, and the card is
       * still the only route of any kind on iOS, which fires no event and offers nothing.
       */
      setDeferred(event as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const isStandalone =
    installed ||
    (typeof window !== 'undefined' &&
      (window.matchMedia?.('(display-mode: standalone)').matches ||
        // Safari's own, older flag; it is the only signal iOS gives.
        (window.navigator as { standalone?: boolean }).standalone === true));

  const offer = installOffer({
    hasPrompt: deferred !== null,
    isIos:
      typeof navigator !== 'undefined' &&
      isIosDevice(navigator.userAgent, navigator.maxTouchPoints ?? 0),
    isStandalone: Boolean(isStandalone),
    dismissedUntil,
    now: Date.now(),
  });

  const install = useCallback(async () => {
    if (!deferred) return false;
    try {
      await deferred.prompt();
      const { outcome } = await deferred.userChoice;
      return outcome === 'accepted';
    } catch {
      /*
       * `prompt()` may only be called once per event, and the browser's own offer can
       * have consumed this one already -- which is now the common case rather than an
       * exotic one, since that offer is no longer suppressed. A rejection here means the
       * dialog cannot be opened again, not that anything is broken, and letting it escape
       * would surface a browser rule as an unhandled rejection in a click handler.
       */
      return false;
    } finally {
      // Single-use whatever happened. Keeping it would leave a button that silently does
      // nothing, which is worse than no button.
      setDeferred(null);
    }
  }, [deferred]);

  const dismiss = useCallback(() => {
    const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
    writeDismissedUntil(until);
    setDismissedUntil(until);
  }, []);

  return { offer, install, dismiss };
}
